import { ClockIcon } from '@heroicons/react/outline';
import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import { Divider, Tag, Tooltip, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import CopyIcon from 'assets/Icons/CopyIcon';
import TimeRunningClock from 'assets/Icons/TimeRunningClock';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import AuthorInfo from '@/components/BookLandingPageComp/HeroSection/AuthorInfo';
import { formatFollowerCount } from '@/components/ChatBox';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { trackAddToCartEvent } from '@/components/shared/Gtags';

import { localUserID, user } from '@/atoms/user';
import { IAuthorInterface } from '@/pages/books/[bookId]';
import { addCouponCode, addToCart, addToRecentlyViewed } from '@/services/Cart';
import { AddToCartProps } from '@/utils/interfaces/cart';
import { User } from '@/utils/interfaces/User';

import { extractSlugAndProductId } from '../SlidBookContainer';
import StarRating from '../StarRating';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import KeyValueTag from '../../shared/keyPoints';
import { notify } from '../../shared/notify';
import BookIcon from '../../../../assets/Icons/BookIcon';
import CertificateIcon from '../../../../assets/Icons/CertificateIcon';
import FrameworkIcon from '../../../../assets/Icons/FrameworkIcon';
import GoogleTranslateIcon from '../../../../assets/Icons/google-translate';
import LevelIcon from '../../../../assets/Icons/LevelIcon';
import { isRatingRefreshAtom } from '../../../atoms/isRatingRefreshAtom';
import { totalReviews } from '../../../atoms/totalReviews';
import { getAverageRating } from '../../../services/ReviewLandingPage';
import {
  IAuthorDetailsList,
  IHeroSectionLandingPage,
} from '../../../utils/interfaces/landingPage';

interface ProductProps {
  heroSectionData?: IHeroSectionLandingPage;
  authorsData: IAuthorInterface | IAuthorInterface[] | undefined;
}

const iconMap = {
  finishIn: <ClockIcon className='w-5 h-5 text-[#737373]' />,
  getCertificate: <CertificateIcon />,
  noOfDesignFrameworks: <FrameworkIcon />,
  language: <GoogleTranslateIcon filled={true} color='#737373' />,
  pageNumber: <BookIcon />,
  level: <LevelIcon />,
};

const formatMap = {
  finishIn: (value: string) => `Finish in ${value}`,
  getCertificate: () => 'Get a Certificate',
  noOfDesignFrameworks: (value: string) => `No. of Design Frameworks: ${value}`,
  language: (value: string) => value,
  pageNumber: (value: string) => `${value} Pages`,
  level: (value: string) => `Level: ${value}`,
};

const HeroSection: React.FC<ProductProps> = ({
  heroSectionData,
  authorsData,
}) => {
  const [rating, setRating] = useState<number>();
  const [tooltipText, setTooltipText] = useState('Copy');
  const [showTooltip, setShowTooltip] = useState(false);
  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });
  const [isRatingRefresh, setIsRatingRefresh] =
    useRecoilState(isRatingRefreshAtom);
  const totalReview = useRecoilValue(totalReviews);
  const localUserId = useRecoilValue(localUserID);
  const screens = useBreakpoint();
  const router = useRouter();
  const productId = extractSlugAndProductId(router.asPath);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userData: User | undefined = useRecoilValue(user);

  const roundToNearestHalf = (num: number) => {
    return Math.round(num * 2) / 2;
  };

  const averageRating = async () => {
    try {
      const data = await getAverageRating(productId);
      setRating(roundToNearestHalf(data.averageRating));
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  useEffect(() => {
    if (productId) {
      averageRating();
      setIsRatingRefresh(false);
    }
  }, [productId, isRatingRefresh]);

  const handleScrollToBottom = () => {
    if (typeof window !== 'undefined') {
      const target = document.getElementById('review-container');
      if (target) {
        const targetPosition =
          target.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleCopy = (couponCode: string) => {
    navigator.clipboard.writeText(couponCode);
    setTooltipText('Copied!');
    setShowTooltip(true);

    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipText('Copy');
      setShowTooltip(false);
    }, 2000);
  };

  const handleApplyCoupon = async (coupon: string) => {
    try {
      await addCouponCode(coupon as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    isBuyNow?: boolean,
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      const data = await addToCart(payload, isBuyNow);
      addToRecentlyViewed(payload.id, userData?.zl_uid);

      notify(
        `${heroSectionData?.bookName} Hard Copy added to cart successfully!`,
        'success',
      );
      if (isBuyNow) {
        await handleApplyCoupon(heroSectionData?.productCouponCode || '');
        router.push(`/checkout?cartId=${data?.data?.cart_id}&pre`);
      } else {
        router.push(`/cart/`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  return (
    <React.Fragment>
      <div className='text-fadedWhite  flex flex-col gap-y-2  lg:w-1/2 xl:w-3/5'>
        <div className='flex gap-y-1 md:gap-y-5 flex-col'>
          <ShouldRender check={screens?.lg}>
            <div className='flex w-min pb-1'>
              <div className='flex w-min '></div>
              <motion.div
                style={{
                  position: 'relative',
                  display: 'inline-block',
                  borderRadius: '4px',
                  padding: '1px',
                  backgroundColor: '#001330',
                }}
              >
                <motion.div
                  initial={{ backgroundPosition: '0% 50%' }}
                  animate={{ backgroundPosition: '200% 50%' }}
                  transition={{
                    ease: 'linear',
                    duration: 3,
                    repeat: Infinity,
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '4px',
                    border: '2px solid transparent',
                    background:
                      'linear-gradient(90deg, rgba(35,122,255,0) 25%, rgba(35,122,255,1) 50%, rgba(35,122,255,0) 75%)',
                    backgroundSize: '200% 100%',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className='text-[#237AFF] whitespace-nowrap bg-[#001330] font-semibold py-1.5 px-3 text-xs flex'
                  style={{
                    borderRadius: '4px',
                    zIndex: 2,
                    position: 'relative',
                  }}
                >
                  {heroSectionData?.productHighlightText}
                </div>
              </motion.div>
            </div>
          </ShouldRender>

          <ShouldRender check={heroSectionData?.isMultipleAuthors}>
            <AuthorInfo
              authorsData={heroSectionData?.authorsList as IAuthorDetailsList[]}
            />
          </ShouldRender>
          <ShouldRender check={!heroSectionData?.isMultipleAuthors}>
            <Typography.Title
              level={3}
              className='text-xs font-light lg:font-normal lg:text-sm text-white leading-5  mt-2 lg:mt-0'
            >
              BY{' '}
              {!Array.isArray(authorsData) && authorsData?.user_name && (
                <>
                  <Link
                    href={`/author/${authorsData.user_name}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex hover:underline text-white'
                    style={{ textUnderlineOffset: '2px' }}
                  >
                    {heroSectionData?.authorName?.toUpperCase()}
                  </Link>
                  <ShouldRender
                    check={Number(authorsData?.follower_count) > 200}
                  >
                    <p className='bg-dropDownMenuBg rounded-full px-3 inline-flex py-0.5 mx-1'>
                      {formatFollowerCount(Number(authorsData?.follower_count))}{' '}
                      Learners
                    </p>
                  </ShouldRender>
                  <span className='inline-flex items-center'>
                    <span className=''>
                      <Divider type='vertical' />
                    </span>

                    <ShouldRender check={heroSectionData?.authorName}>
                      <Image
                        alt={heroSectionData?.authorName || 'company_icon'}
                        src={heroSectionData?.companyIcon as string}
                        width={screens.lg ? 16 : 14}
                        height={screens.lg ? 16 : 14}
                        className='ml-1 mr-2'
                      />
                    </ShouldRender>
                  </span>
                </>
              )}
              {heroSectionData?.authorDesignation}
            </Typography.Title>
          </ShouldRender>

          <div className='flex flex-col mt-2'>
            <Typography.Title
              level={1}
              className='text-lg lg:text-[26px] font-semibold'
            >
              {heroSectionData?.productTitle}
            </Typography.Title>
            <span className='text-sm md:text-base font-medium text-[#E2E2E2] mt-2 md:mt-3'>
              {heroSectionData?.shortDescription}
            </span>
          </div>
        </div>

        <div className='flex flex-col lg:items-center lg:flex-row gap-3 lg:gap-5 mt-2 '>
          <ShouldRender check={rating}>
            <span className='flex items-center text-sm md:text-base lg:text-xl gap-x-2'>
              <span className=''>
                <StarRating rating={rating || 0} color='#2DD598' />
              </span>
              <span className='font-medium text-sm '>{rating || 0}</span>
              <span className=''>
                <Divider type='vertical' />
              </span>
              <span
                onClick={handleScrollToBottom}
                className='font-medium text-xs md:text-sm underline cursor-pointer'
              >
                {totalReview} Reviews
              </span>
            </span>
          </ShouldRender>
          <ShouldRender check={heroSectionData?.bookType === 'comingSoon'}>
            <div className='inline-flex flex-shrink-0 mt-4 items-center h-11'>
              <div className='flex flex-col items-start'>
                <svg
                  width={5}
                  height={7}
                  viewBox='0 0 5 7'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M-2.18557e-07 2L5 -3L5 7L-2.18557e-07 2Z'
                    fill='#282829'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M-2.18557e-07 5L5 7.35117e-07L5 10L-2.18557e-07 5Z'
                    fill='#282829'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M-2.18557e-07 5L5 7.35117e-07L5 10L-2.18557e-07 5Z'
                    fill='#282829'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M-2.18557e-07 5L5 7.35117e-07L5 10L-2.18557e-07 5Z'
                    fill='#282829'
                  />
                </svg>
                <svg
                  width={5}
                  height={7}
                  viewBox='0 0 5 7'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M-2.18557e-07 5L5 7.35117e-07L5 10L-2.18557e-07 5Z'
                    fill='#282829'
                  />
                </svg>
              </div>

              <div className='flex items-center  p-0  h-full bg-grayZl-950'>
                <div className='flex  items-center  gap-2  h-full border-none px-6 bg-[#282829] text-grayZl-600 font-light leading-6'>
                  Coupon
                </div>
                <div className='border-l border-[#77777c] border-dashed h-11' />
                <div className='flex items-center gap-2 pt-[0.5625rem] pb-[0.5625rem] px-6'>
                  <div className='flex flex-col uppercase items-center gap-2.5 text-white  leading-6'>
                    {heroSectionData?.productCouponCode}
                  </div>
                  <Tooltip
                    className='cursor-pointer'
                    title={tooltipText}
                    open={showTooltip}
                  >
                    <motion.span
                      onClick={() =>
                        handleCopy(heroSectionData?.productCouponCode || '')
                      }
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <CopyIcon />
                    </motion.span>
                  </Tooltip>
                </div>
              </div>
              <div className='flex flex-col items-start'>
                <svg
                  width={5}
                  height={7}
                  viewBox='0 0 5 7'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M5 2L-3.97233e-08 -3L-4.76837e-07 7L5 2Z'
                    fill='#121212'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M5 5L-3.97233e-08 7.35117e-07L-4.76837e-07 10L5 5Z'
                    fill='#121212'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M5 5L-3.97233e-08 7.35117e-07L-4.76837e-07 10L5 5Z'
                    fill='#121212'
                  />
                </svg>
                <svg
                  width={5}
                  height={10}
                  viewBox='0 0 5 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M5 5L-3.97233e-08 7.35117e-07L-4.76837e-07 10L5 5Z'
                    fill='#121212'
                  />
                </svg>
                <svg
                  width={5}
                  height={7}
                  viewBox='0 0 5 7'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M5 5L-3.97233e-08 7.35117e-07L-4.76837e-07 10L5 5Z'
                    fill='#121212'
                  />
                </svg>
              </div>
            </div>
            <ShouldRender check={!screens?.lg}>
              <div className={clsx(' text-white pt-4 rounded-md ')}>
                <div className='flex items-center justify-start font-light space-x-1 mb-3 text-sm text-[#F87171] '>
                  <TimeRunningClock />
                  <span className='text-sm leading-[1.375rem]'>
                    Prebook Now, Pay the Lowest
                  </span>
                </div>

                <div className='text-white text-sm font-light text-start mb-2 leading-[1.375rem]'>
                  Hard Copy + Digital Copy
                </div>

                <div className='flex items-end justify-start mb-5 space-x-2'>
                  <span className='flex xxxs:gap-x-1 md:gap-x-2 items-center pl-1'>
                    <p
                      className='text-white text-2xl font-semibold leading-8 line-through'
                      style={{ textDecorationColor: 'red' }}
                    >
                      ₹{heroSectionData?.hardCopyPrice?.discountedPrice}
                    </p>
                    <p
                      className={clsx(
                        'line-through text-white/[.60] text-center font-medium leading-6 ',
                      )}
                    >
                      ₹{heroSectionData?.hardCopyPrice?.mrpPrice}
                    </p>
                    {heroSectionData?.hardCopyPrice?.discountedPrice &&
                    heroSectionData?.hardCopyPrice?.mrpPrice ? (
                      <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-[10px] md:text-sm items-center'>
                        {Math.round(
                          ((heroSectionData?.hardCopyPrice?.mrpPrice -
                            Number(
                              heroSectionData?.hardCopyPrice?.discountedPrice,
                            )) /
                            heroSectionData?.hardCopyPrice?.mrpPrice) *
                            100,
                        )}
                        %{' '}
                        <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                      </p>
                    ) : null}
                  </span>
                </div>
              </div>
              <div className=' -translate-y-4 xl:-translate-y-0'>
                <ButtonDefault
                  isWidthFull={true}
                  size={4}
                  variant={ButtonVariants.PRIMARY}
                  is3D={true}
                  loading={loadingAddToCart.value}
                  disabled={loadingAddToCart.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddDataToCart(
                      {
                        id: Number(
                          heroSectionData?.hardCoverProductId,
                        ) as number,
                        temp_user_id: localUserId,
                        quantity: 1,
                      },
                      true,
                    );

                    trackAddToCartEvent({
                      productData: {
                        price: Number(
                          heroSectionData?.hardCopyPrice?.discountedPrice,
                        ),
                        id: Number(heroSectionData?.hardCoverProductId),
                        heading: `${heroSectionData?.bookName} 'Hard Copy'`,
                        quantity: 1 as number,
                      },
                      userData,
                      pathname: router.pathname,
                      productType: 'hardcopy',
                    });
                  }}
                  className='w-full p-0 py-0 px-0'
                >
                  <div className='w-full flex justify-center font-semibold leading-6 items-center gap-1 py-[3px] px-3.5'>
                    Pre-Order at ₹
                    {heroSectionData?.hardCopyPrice?.comingSoonPrice}
                  </div>
                </ButtonDefault>
                <div className='flex justify-start mt-4'>
                  <ul className='text-white text-sm font-light space-y-1 list-disc list-inside'>
                    <li>Digital Book will be delivered instantly.</li>
                    <li>Pre Orders will be shipped in 15-30 days.</li>
                  </ul>
                </div>
              </div>
              <div className='self-stretch h-px bg-white/[.20]'></div>
            </ShouldRender>
          </ShouldRender>

          {/* <ShouldRender check={screens.lg}>
            <span className=''>
              <Divider type='vertical' />
            </span>
          </ShouldRender> */}

          {/* <div className='flex flex-row gap-x-5'>
            <span className='flex items-center text-sm md:text-base lg:text-xl gap-x-2'>
              <span className=' text-gray-300'>
                <GoogleTranslateIcon filled={true} />
              </span>
              <span className='font-medium text-sm'>
                {heroSectionData?.language}
              </span>
            </span>

            <span className='flex items-center text-sm md:text-base lg:text-xl gap-x-2'>
              <span className='text-gray-300'>
                <OpenedBookIcon />
              </span>
              <span className='font-medium text-sm'>
                {' '}
                {heroSectionData?.numberOfPages} Pages
              </span>
            </span>
          </div> */}
        </div>
        <ShouldRender check={heroSectionData?.keyPoints}>
          <div className={clsx('flex flex-wrap gap-2 mt-5')}>
            {heroSectionData?.keyPoints?.map((keyPoint, index) => {
              return (
                <Tag
                  className='border-none rounded-lg px-3 md:px-5 py-2 m-0 bg-[#202020] text-qureosSecondary'
                  key={`${keyPoint.name}-${index}`}
                >
                  <KeyValueTag
                    key={index}
                    name={keyPoint.name}
                    value={keyPoint.value ?? ''}
                    iconMap={iconMap}
                    formatMap={formatMap}
                  />
                </Tag>
              );
            })}
          </div>
        </ShouldRender>
      </div>
    </React.Fragment>
  );
};
export default HeroSection;
